import { Suspense, lazy } from 'react'
import { ClipboardList } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { CreateMenuItem } from './createRegistry'
import { useUserTimezone } from '@/hooks/useUserTimezone'
import { ReadonlyModeProvider } from '@/contexts/ReadonlyModeContext'
import { CreateFlyout } from '@/components/ui/create-flyout'

/** 哨兵：深链 `?create=task` 打开通用建任务（Quick create，类型留空） */
export const TASK_ITEM: CreateMenuItem = {
  id: 'task',
  group: 'tasks',
  labelKey: 'item.task',
  icon: ClipboardList,
  perm: 'pms:task:create',
  kind: 'flyout',
}

const IntentCreateForm = lazy(() =>
  import('@/components/intents/IntentCreateForm').then((m) => ({ default: m.IntentCreateForm })),
)
const QuickCreateDrawer = lazy(() =>
  import('@/features/quick-create/QuickCreateDrawer').then((m) => ({ default: m.QuickCreateDrawer })),
)
const NewUpsellRequestForm = lazy(() =>
  import('@/components/upsellRequests/NewUpsellRequestForm').then((m) => ({ default: m.NewUpsellRequestForm })),
)
const ReservationCreateForm = lazy(() =>
  import('@/components/reservations/ReservationCreateForm').then((m) => ({ default: m.ReservationCreateForm })),
)
const PurchaseCreateDialog = lazy(() =>
  import('@/components/procurement/PurchaseCreateDialog').then((m) => ({ default: m.PurchaseCreateDialog })),
)
const NewAssetDialog = lazy(() =>
  import('@/components/asset-mgr/location/NewAssetDialog').then((m) => ({ default: m.NewAssetDialog })),
)
const UserCreateDrawer = lazy(() =>
  import('@/components/user/user-create-drawer').then((m) => ({ default: m.UserCreateDrawer })),
)
const ProductDetail = lazy(() =>
  import('@/components/asset-mgr/ProductDetail').then((m) => ({ default: m.ProductDetail })),
)
const NewPlaceDialog = lazy(() =>
  import('@/components/listing/NewPlaceDialog').then((m) => ({ default: m.NewPlaceDialog })),
)
const AnnouncementEditor = lazy(() =>
  import('@/features/announcements/admin/AnnouncementEditor').then((m) => ({ default: m.AnnouncementEditor })),
)

interface CreateMenuHostProps {
  activeItem: CreateMenuItem | null
  onClose: () => void
}

const isTaskItem = (id: string) => id === 'task' || id.startsWith('task.')

export function CreateMenuHost({ activeItem, onClose }: CreateMenuHostProps) {
  const userTimezone = useUserTimezone()
  const { t } = useTranslation('create')

  if (!activeItem) return null
  const close = (open: boolean) => { if (!open) onClose() }

  if (activeItem.id === 'quick_create') {
    return (
      <Suspense fallback={null}>
        <QuickCreateDrawer open onClose={onClose} />
      </Suspense>
    )
  }

  if (activeItem.id === 'intent') {
    return (
      <Suspense fallback={null}>
        <IntentCreateForm
          open
          onOpenChange={close}
          selectedIntentType="SR"
          onIntentCreated={onClose}
        />
      </Suspense>
    )
  }

  if (isTaskItem(activeItem.id)) {
    // 建任务一律走 Quick create（F2：老表单的新建退役）；Task · Cleaning 这类菜单项预选类型
    return (
      <Suspense fallback={null}>
        <QuickCreateDrawer open onClose={onClose} initialMode="task" initialTaskTypeCode={activeItem.taskType} />
      </Suspense>
    )
  }

  if (activeItem.id === 'upsell') {
    return (
      <Suspense fallback={null}>
        <NewUpsellRequestForm open onOpenChange={close} onSuccess={onClose} userTimezone={userTimezone} />
      </Suspense>
    )
  }


  if (activeItem.id === 'reservation') {
    return (
      <Suspense fallback={null}>
        <ReservationCreateForm isOpen onClose={onClose} onCreated={onClose} />
      </Suspense>
    )
  }

  if (activeItem.id === 'purchase') {
    return (
      <Suspense fallback={null}>
        <PurchaseCreateDialog open onOpenChange={close} onSuccess={onClose} />
      </Suspense>
    )
  }

  if (activeItem.id === 'asset') {
    return (
      <Suspense fallback={null}>
        <NewAssetDialog open onOpenChange={close} onDone={onClose} />
      </Suspense>
    )
  }

  if (activeItem.id === 'user') {
    return (
      <Suspense fallback={null}>
        <UserCreateDrawer open onOpenChange={close} onUserCreated={onClose} />
      </Suspense>
    )
  }

  if (activeItem.id === 'product') {
    return (
      <ReadonlyModeProvider>
        <CreateFlyout open onClose={onClose} title={t('item.product')} fitContent>
          <Suspense fallback={null}>
            <ProductDetail product={null} onSaved={onClose} onDeleted={onClose} onCancel={onClose} />
          </Suspense>
        </CreateFlyout>
      </ReadonlyModeProvider>
    )
  }

  if (activeItem.id === 'place.company' || activeItem.id === 'place.virtual') {
    return (
      <Suspense fallback={null}>
        <NewPlaceDialog
          open
          onOpenChange={(open) => { if (!open) onClose() }}
          kind={activeItem.id === 'place.company' ? 'COMPANY' : 'VIRTUAL'}
        />
      </Suspense>
    )
  }

  if (activeItem.id === 'announcement') {
    return (
      <CreateFlyout open onClose={onClose} title={t('item.announcement')}>
        <div className="p-6">
          <Suspense fallback={null}>
            <AnnouncementEditor announcementId={null} onClose={onClose} onSaved={onClose} />
          </Suspense>
        </div>
      </CreateFlyout>
    )
  }

  return null
}
